// components/VitalsChart.jsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';
import { VITAL_CONFIG, VitalIcon } from './vitalsConfig';
import { parseBackendDate } from '../utils/datetime';
import { CalendarDays, PenLine, Smartphone, BarChart3, CircleAlert, RefreshCw, ChevronUp, ChevronDown } from 'lucide-react';

// ============== HELPER COMPONENTS ==============

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, selectedVital }) => {
    if (!active || !payload || !payload.length) return null;
    const config = VITAL_CONFIG[selectedVital];

    return (
        <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-xl p-4 min-w-[180px]">
            <p className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-100 inline-flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5 text-teal-600" strokeWidth={1.75} />
                {label}
            </p>
            {selectedVital === 'Blood Pressure' ? (
                <div className="space-y-2">
                    {payload.map((entry, index) => (
                        <div key={index} className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <span
                                    className="w-3 h-3 rounded-full shadow-sm"
                                    style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-sm text-gray-600">{entry.name}</span>
                            </div>
                            <span className="text-sm font-bold text-gray-800">
                                {entry.value}
                            </span>
                        </div>
                    ))}
                    <div className="pt-2 mt-2 border-t border-gray-100 text-center">
                        <span className="text-lg font-bold text-gray-800">
                            {payload[0]?.value}/{payload[1]?.value}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">{config.unit}</span>
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <span
                            className="w-3 h-3 rounded-full shadow-sm"
                            style={{ backgroundColor: config.color }}
                        />
                        <span className="text-sm text-gray-600">{config.label}</span>
                    </div>
                    <div>
                        <span className="text-lg font-bold text-gray-800">{payload[0]?.value}</span>
                        <span className="text-sm text-gray-500 ml-1">{config.unit}</span>
                    </div>
                </div>
            )}
            {payload[0]?.payload?.source && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-400">Source: {payload[0].payload.source}</span>
                </div>
            )}
        </div>
    );
};

// Stats Card Component
const StatsCard = ({ title, value, unit, color, trend }) => (
    <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</span>
            {trend !== undefined && trend !== 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                    {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
                </span>
            )}
        </div>
        <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold" style={{ color }}>{value}</span>
            <span className="text-sm text-gray-400">{unit}</span>
        </div>
    </div>
);

// Loading Skeleton Component
const ChartSkeleton = () => (
    <div className="flex flex-col items-center justify-center h-80 gap-3">
        <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
        <p className="text-gray-600 font-medium">Loading vitals data...</p>
    </div>
);

// Empty State Component
const EmptyState = ({ selectedVital }) => (
    <div className="flex flex-col items-center justify-center h-80 text-gray-500">
        <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <BarChart3 className="w-10 h-10 text-gray-300" strokeWidth={1.5} aria-hidden="true" />
        </div>
        <p className="text-lg font-medium text-gray-700">No data available</p>
        <p className="text-sm text-gray-400 mt-1">
            No {selectedVital.toLowerCase()} readings found for the selected time range
        </p>
    </div>
);

// Error Component
const ErrorMessage = ({ error, onRetry }) => (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
        <div className="p-2 bg-red-100 rounded-lg">
            <CircleAlert className="w-5 h-5 text-red-500" strokeWidth={2} aria-hidden="true" />
        </div>
        <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Failed to load data</p>
            <p className="text-xs text-red-600">{error}</p>
        </div>
        <button
            onClick={onRetry}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors"
        >
            Retry
        </button>
    </div>
);

// Blood Pressure Legend Component
const BPLegend = ({ config }) => (
    <div className="flex justify-center gap-8 mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
            <div className="w-8 h-1 rounded-full" style={{ backgroundColor: config.systolicColor }} />
            <span className="text-sm text-gray-600">Systolic (top number)</span>
        </div>
        <div className="flex items-center gap-2">
            <div className="w-8 h-1 rounded-full" style={{ backgroundColor: config.diastolicColor }} />
            <span className="text-sm text-gray-600">Diastolic (bottom number)</span>
        </div>
    </div>
);

// ============== DATA TABLE COMPONENT ==============
const DataTable = ({ data, selectedVital, config }) => {
    const [sortField, setSortField] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            let aVal, bVal;

            if (sortField === 'timestamp') {
                aVal = new Date(a.fullDate);
                bVal = new Date(b.fullDate);
            } else if (sortField === 'value') {
                if (selectedVital === 'Blood Pressure') {
                    aVal = a.systolic || 0;
                    bVal = b.systolic || 0;
                } else {
                    aVal = a.value || 0;
                    bVal = b.value || 0;
                }
            } else if (sortField === 'source') {
                aVal = a.source || '';
                bVal = b.source || '';
            }

            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            }
            return aVal < bVal ? 1 : -1;
        });
    }, [data, sortField, sortDirection, selectedVital]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return sortedData.slice(start, start + itemsPerPage);
    }, [sortedData, currentPage]);

    const totalPages = Math.ceil(data.length / itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const SortIcon = ({ field }) => (
        <span className="ml-1 inline-block">
            {sortField === field ? (
                sortDirection === 'asc' ? '↑' : '↓'
            ) : (
                <span className="text-gray-300">↕</span>
            )}
        </span>
    );

    const getStatusBadge = (reading) => {
        if (selectedVital === 'Blood Pressure') {
            const systolic = reading.systolic;
            const diastolic = reading.diastolic;
            if (systolic > 140 || diastolic > 90) {
                return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">High</span>;
            } else if (systolic < 90 || diastolic < 60) {
                return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">Low</span>;
            }
            return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Normal</span>;
        }

        const value = reading.value;
        const normalRange = config.normalRange;

        if (!normalRange) return null;

        if (normalRange.max && value > normalRange.max) {
            return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">High</span>;
        } else if (normalRange.min && value < normalRange.min) {
            return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">Low</span>;
        }
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Normal</span>;
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Table Header */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-800 inline-flex items-center gap-2">
                        <VitalIcon icon={config.icon} className="w-5 h-5 text-teal-700" />
                        {selectedVital} Readings
                    </h4>
                    <span className="text-sm text-gray-500">
                        {data.length} total readings
                    </span>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                            <th
                                className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('timestamp')}
                            >
                                Date & Time <SortIcon field="timestamp" />
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('value')}
                            >
                                Value <SortIcon field="value" />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Status
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('source')}
                            >
                                Source <SortIcon field="source" />
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {paginatedData.map((reading, index) => (
                            <tr key={reading.id || index} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                        {new Date(reading.fullDate).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric'
                                        })}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {new Date(reading.fullDate).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {selectedVital === 'Blood Pressure' ? (
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg font-bold text-gray-900">
                                                {reading.systolic}/{reading.diastolic}
                                            </span>
                                            <span className="text-sm text-gray-500">{config.unit}</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg font-bold" style={{ color: config.color }}>
                                                {reading.value}
                                            </span>
                                            <span className="text-sm text-gray-500">{config.unit}</span>
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getStatusBadge(reading)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${reading.source?.includes('BLE') || reading.source?.includes('ble')
                                            ? 'bg-blue-100 text-blue-800'
                                            : reading.source?.includes('manual') || reading.source?.includes('Manual')
                                                ? 'bg-purple-100 text-purple-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                        {reading.source?.includes('BLE') || reading.source?.includes('ble') ? (
                                            <span className="inline-flex items-center gap-1">
                                                <Smartphone className="w-3 h-3" strokeWidth={1.75} />
                                                BLE Device
                                            </span>
                                        ) : reading.source?.includes('manual') || reading.source?.includes('Manual') ? (
                                            <span className="inline-flex items-center gap-1">
                                                <PenLine className="w-3 h-3" strokeWidth={1.75} />
                                                Manual
                                            </span>
                                        ) : (
                                            reading.source || 'Unknown'
                                        )}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, data.length)} of {data.length} readings
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <div className="flex items-center gap-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`px-3 py-1.5 text-sm rounded-lg ${currentPage === pageNum
                                                ? 'bg-teal-600 text-white'
                                                : 'border border-gray-200 hover:bg-gray-100'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ============== MAIN COMPONENT ==============

const VitalsChart = ({ pmrn, API_BASE_URL }) => {
  // State Management
  const [selectedVital, setSelectedVital] = useState("Blood Pressure");
  const [selectedTimeRange, setSelectedTimeRange] = useState("30");
  // const [showNormalRange, setShowNormalRange] = useState(true); // Removed - normal range lines disabled
  const [showTable, setShowTable] = useState(false);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep timestamp source consistent with the rest of the app.
  // `created_at` is the canonical persisted timestamp used in patient details.
  // Fall back to `time_entered` only if `created_at` is missing.
  const getReadingTimestamp = useCallback(
      (reading) => reading?.created_at || reading?.time_entered || null,
      []
  );

  // ============== DATA FETCHING ==============
  const fetchReadings = useCallback(async () => {
    if (!pmrn) {
      setError("Patient MRN is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        order_by: 'desc'
      });

      const response = await axios.get(
        `${API_BASE_URL}/readings/patient/${pmrn}?${params.toString()}`
      );

      // Normalize response shape
      const list = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];

      setReadings(list);

    } catch (err) {
      console.error("❌ Fetch error:", err);
      setError(err.message || "Failed to fetch readings");
    } finally {
      setLoading(false);
    }
  }, [pmrn, API_BASE_URL]);

  // Fetch on mount + pmrn change
  useEffect(() => {
    fetchReadings();
  }, [fetchReadings]);

  // ============== DATA PROCESSING - INDIVIDUAL ENTRIES ==============
  const chartData = useMemo(() => {
      if (!readings || readings.length === 0) return [];

      const config = VITAL_CONFIG[selectedVital];

      // Client-side filtering by selected time range
      const daysAgo = parseInt(selectedTimeRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

      const filteredReadings = readings.filter(reading => {
          const readingDate = parseBackendDate(getReadingTimestamp(reading));
          return readingDate && readingDate >= cutoffDate;
      });

      // Process filtered readings
      const processedData = filteredReadings
          .map((reading, index) => {
              const date = parseBackendDate(getReadingTimestamp(reading)) || new Date();
              const timeLabel = date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
              }) + ' ' + date.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
              });

              if (selectedVital === 'Blood Pressure') {
                  const systolic = reading.systolic_bp;
                  const diastolic = reading.diastolic_bp;

                  if (systolic == null || diastolic == null || systolic <= 0 || diastolic <= 0) {
                      return null;
                  }

                  return {
                      id: reading.id,
                      index,
                      time: timeLabel,
                      fullDate: date,
                      systolic: Number(systolic),
                      diastolic: Number(diastolic),
                      source: reading.entry_source || reading.taken_by_user_id,
                  };
              } else {
                  const value = reading[config.key];

                  if (value == null ||
                    (
                        selectedVital !== 'Steps' &&
                        selectedVital !== 'Sleep Hours' &&
                        selectedVital !== 'Workouts' &&
                        selectedVital !== 'Calories' && value <= 0
                    )) {
                      return null;
                  }

                  return {
                      id: reading.id,
                      index,
                      time: timeLabel,
                      fullDate: date,
                      value: Number(value),
                      source: reading.entry_source || reading.taken_by_user_id,
                  };
              }
          })
          .filter(item => item !== null)
          .sort((a, b) => a.fullDate - b.fullDate);

      //console.log('Processed chart data (individual entries):', processedData.length);
      return processedData;
  }, [readings, selectedVital, selectedTimeRange, getReadingTimestamp]);

  // ============== STATS CALCULATION ==============
  const stats = useMemo(() => {
      if (chartData.length === 0) return null;

      const config = VITAL_CONFIG[selectedVital];

      if (selectedVital === 'Blood Pressure') {
          const systolicValues = chartData.map(d => d.systolic).filter(v => v !== null);
          const diastolicValues = chartData.map(d => d.diastolic).filter(v => v !== null);

          if (systolicValues.length === 0 || diastolicValues.length === 0) return null;

          const latestSystolic = systolicValues[systolicValues.length - 1];
          const latestDiastolic = diastolicValues[diastolicValues.length - 1];
          const avgSystolic = Math.round(systolicValues.reduce((a, b) => a + b, 0) / systolicValues.length);
          const avgDiastolic = Math.round(diastolicValues.reduce((a, b) => a + b, 0) / diastolicValues.length);

          return {
              latest: `${latestSystolic}/${latestDiastolic}`,
              average: `${avgSystolic}/${avgDiastolic}`,
              maxSystolic: Math.max(...systolicValues),
              minSystolic: Math.min(...systolicValues),
              maxDiastolic: Math.max(...diastolicValues),
              minDiastolic: Math.min(...diastolicValues),
              unit: config.unit,
              count: systolicValues.length
          };
      }

      const values = chartData.map(d => d.value).filter(v => v !== null);
      if (values.length === 0) return null;

      const latest = values[values.length - 1];
      const previous = values.length > 1 ? values[values.length - 2] : latest;
      const trend = previous !== 0 ? Math.round(((latest - previous) / previous) * 100) : 0;

      return {
          latest,
          average: Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10,
          max: Math.max(...values),
          min: Math.min(...values),
          unit: config.unit,
          count: values.length,
          trend
      };
  }, [chartData, selectedVital]);

  // ============== Y-AXIS DOMAIN CALCULATION ==============
  const yAxisDomain = useMemo(() => {
      const config = VITAL_CONFIG[selectedVital];

      if (config.domain === 'auto' || (Array.isArray(config.domain) && config.domain.includes('auto'))) {
          if (chartData.length === 0) return [0, 100];

          let values;
          if (selectedVital === 'Blood Pressure') {
              values = [
                  ...chartData.map(d => d.systolic),
                  ...chartData.map(d => d.diastolic)
              ].filter(v => v !== null);
          } else {
              values = chartData.map(d => d.value).filter(v => v !== null);
          }

          if (values.length === 0) return [0, 100];

          const min = Math.min(...values);
          const max = Math.max(...values);
          const padding = (max - min) * 0.15 || 10;

          const domainMin = config.domain === 'auto'
              ? Math.floor(min - padding)
              : (Array.isArray(config.domain) ? config.domain[0] : 0);

          return [Math.max(0, domainMin), Math.ceil(max + padding)];
      }

      return config.domain;
  }, [selectedVital, chartData]);

  const currentConfig = VITAL_CONFIG[selectedVital];

  // ============== RENDER ==============
  return (
      <div className="space-y-6">
          {/* Error Display */}
          {error && <ErrorMessage error={error} onRetry={fetchReadings} />}

          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                  <h3 className="text-2xl font-bold text-gray-800">Historical Trends</h3>
                  <p className="text-sm text-gray-500 mt-1">
                      Track your vital signs over time • Each reading displayed individually
                  </p>
              </div>
              <div className="flex items-center gap-4">
                  <button
                      onClick={fetchReadings}
                      disabled={loading}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                                 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all disabled:opacity-50"
                  >
                      <RefreshCw
                          className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                          strokeWidth={2}
                          aria-hidden="true"
                      />
                      {loading ? 'Loading...' : 'Refresh'}
                  </button>

                  {/* <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                          type="checkbox"
                          checked={showNormalRange}
                          onChange={(e) => setShowNormalRange(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      Show Normal Range
                  </label> */}
              </div>
          </div>

          {/* Vitals Selection Pills */}
          <div className="flex flex-wrap items-center gap-2">
              {Object.keys(VITAL_CONFIG).map((vital) => {
                  const config = VITAL_CONFIG[vital];
                  const isSelected = vital === selectedVital;
                  const primaryColor = vital === 'Blood Pressure' ? config.systolicColor : config.color;

                  return (
                      <button
                          key={vital}
                          onClick={() => setSelectedVital(vital)}
                          className={`
                              px-4 py-2.5 rounded-xl border-2 transition-all duration-200 font-medium text-sm
                              flex items-center gap-2
                              ${isSelected
                                  ? 'text-white shadow-lg scale-105'
                                  : 'text-gray-600 border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                              }
                          `}
                          style={isSelected ? {
                              backgroundColor: primaryColor,
                              borderColor: primaryColor
                          } : {}}
                      >
                          <VitalIcon
                            icon={config.icon}
                            className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-teal-700'}`}
                          />
                          <span>{vital}</span>
                          <span className={`text-xs ${isSelected ? 'text-white/70' : 'text-gray-400'}`}>
                              ({config.unit})
                          </span>
                      </button>
                  );
              })}
          </div>

          {/* Stats & Time Range Row */}
          <div className="flex flex-wrap items-start justify-between gap-4">
              {!loading && stats && (
                  <div className="flex flex-wrap gap-3">
                      {selectedVital === 'Blood Pressure' ? (
                          <>
                              <StatsCard
                                  title="Latest"
                                  value={stats.latest}
                                  unit={stats.unit}
                                  color={currentConfig.systolicColor}
                              />
                              <StatsCard
                                  title="Average"
                                  value={stats.average}
                                  unit={stats.unit}
                                  color="#6b7280"
                              />
                              <StatsCard
                                  title="Readings"
                                  value={stats.count}
                                  unit="entries"
                                  color="#6b7280"
                              />
                          </>
                      ) : (
                          <>
                              <StatsCard
                                  title="Latest"
                                  value={stats.latest}
                                  unit={stats.unit}
                                  color={currentConfig.color}
                                  trend={stats.trend}
                              />
                              <StatsCard
                                  title="Average"
                                  value={stats.average}
                                  unit={stats.unit}
                                  color="#6b7280"
                              />
                              <StatsCard
                                  title="Range"
                                  value={`${stats.min} - ${stats.max}`}
                                  unit={stats.unit}
                                  color="#6b7280"
                              />
                              <StatsCard
                                  title="Readings"
                                  value={stats.count}
                                  unit="entries"
                                  color="#6b7280"
                              />
                          </>
                      )}
                  </div>
              )}

              <select
                  value={selectedTimeRange}
                  onChange={(e) => setSelectedTimeRange(e.target.value)}
                  className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white shadow-sm
                             focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer
                             hover:border-gray-300 transition-colors"
              >
                  <option value="7">Last 7 Days</option>
                  <option value="14">Last 14 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 3 Months</option>
                  <option value="180">Last 6 Months</option>
                  <option value="365">Last Year</option>
              </select>
          </div>

          {/* Chart Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              {loading ? (
                  <ChartSkeleton />
              ) : chartData.length === 0 ? (
                  <EmptyState selectedVital={selectedVital} />
              ) : (
                  <>
                      <ResponsiveContainer width="100%" height={380}>
                          <LineChart
                              data={chartData}
                              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                          >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                              <XAxis
                                  dataKey="time"
                                  axisLine={{ stroke: '#e5e7eb' }}
                                  tickLine={false}
                                  tick={{ fill: '#6b7280', fontSize: 10 }}
                                  angle={-45}
                                  textAnchor="end"
                                  height={60}
                                  interval={Math.max(0, Math.floor(chartData.length / 10) - 1)}
                              />
                              <YAxis
                                  domain={yAxisDomain}
                                  axisLine={false}
                                  tickLine={false}
                                  tick={{ fill: '#6b7280', fontSize: 12 }}
                                  tickCount={currentConfig.tickCount}
                                  width={50}
                                  tickFormatter={(value) =>
                                      selectedVital === 'Steps'
                                          ? `${(value / 1000).toFixed(0)}k`
                                          : value
                                  }
                              />

                              {/* Normal range reference lines removed */}

                              <Tooltip
                                  content={<CustomTooltip selectedVital={selectedVital} />}
                                  cursor={{ stroke: '#e5e7eb', strokeWidth: 1 }}
                              />

                              {selectedVital === 'Blood Pressure' ? (
                                  <>
                                      <Line
                                          type="monotone"
                                          dataKey="systolic"
                                          name="Systolic"
                                          stroke={currentConfig.systolicColor}
                                          strokeWidth={2}
                                          dot={{ r: 3, fill: currentConfig.systolicColor, strokeWidth: 2, stroke: '#fff' }}
                                          activeDot={{ r: 6, fill: currentConfig.systolicColor, stroke: '#fff', strokeWidth: 2 }}
                                          connectNulls
                                      />
                                      <Line
                                          type="monotone"
                                          dataKey="diastolic"
                                          name="Diastolic"
                                          stroke={currentConfig.diastolicColor}
                                          strokeWidth={2}
                                          dot={{ r: 3, fill: currentConfig.diastolicColor, strokeWidth: 2, stroke: '#fff' }}
                                          activeDot={{ r: 6, fill: currentConfig.diastolicColor, stroke: '#fff', strokeWidth: 2 }}
                                          connectNulls
                                      />
                                  </>
                              ) : (
                                  <Line
                                      type="monotone"
                                      dataKey="value"
                                      name={currentConfig.label}
                                      stroke={currentConfig.color}
                                      strokeWidth={2}
                                      dot={{ r: 3, fill: currentConfig.color, strokeWidth: 2, stroke: '#fff' }}
                                      activeDot={{ r: 6, fill: currentConfig.color, stroke: '#fff', strokeWidth: 2 }}
                                      connectNulls
                                  />
                              )}
                          </LineChart>
                      </ResponsiveContainer>

                      {selectedVital === 'Blood Pressure' && <BPLegend config={currentConfig} />}
                  </>
              )}
          </div>

          {/* View Table Toggle Button */}
          {chartData.length > 0 && (
              <div className="flex justify-center">
                  <button
                      onClick={() => setShowTable(!showTable)}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500
                                 text-white rounded-xl font-medium shadow-md hover:shadow-lg
                                 transform hover:scale-105 transition-all duration-200"
                  >
                      {showTable ? (
                          <>
                              <ChevronUp className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                              Hide Data Table
                          </>
                      ) : (
                          <>
                              <ChevronDown className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                              View Data Table ({chartData.length} readings)
                          </>
                      )}
                  </button>
              </div>
          )}

          {/* Data Table */}
          {showTable && chartData.length > 0 && (
              <DataTable
                  data={chartData}
                  selectedVital={selectedVital}
                  config={currentConfig}
              />
          )}

          {/* Info Card for Normal Ranges
          {currentConfig.normalRange && (
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
                  <div className="flex items-start gap-3">
                      <div className="p-2 bg-teal-100 rounded-lg">
                          <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                          </svg>
                      </div>
                      <div>
                          <h4 className="font-semibold text-teal-800">
                              Normal Range for {selectedVital}
                          </h4>
                          <p className="text-sm text-teal-700 mt-1">
                              {selectedVital === 'Blood Pressure'
                                  ? `Systolic: ${currentConfig.normalRange.systolic.min}-${currentConfig.normalRange.systolic.max} ${currentConfig.unit} | Diastolic: ${currentConfig.normalRange.diastolic.min}-${currentConfig.normalRange.diastolic.max} ${currentConfig.unit}`
                                  : selectedVital === 'Steps'
                                      ? `Target: ${currentConfig.normalRange.min?.toLocaleString()}+ ${currentConfig.unit} per day`
                                      : `${currentConfig.normalRange.min} - ${currentConfig.normalRange.max} ${currentConfig.unit}`
                              }
                          </p>
                      </div>
                  </div>
              </div>
          )} */}
      </div>
  );
};

export default VitalsChart;
